import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import PageLoading from "components/loading/PageLoading";
import SearchInput from "components/SearchInput";
import StackLockNotice from "components/StackLockNotice";
import PageWrapper from "components/PageWrapper";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateTask from "shared/Stack/access/useCanCreateTask";
import { STACK_TASKS_FILTER_TYPE } from "shared/Stack/useSearchTasksSuggestions/constants";
import { AccountContext } from "views/AccountWrapper";

import StackDetachedIntegrationCallout from "../components/DetachedIntegrationCallout";
import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import NewTaskForm from "./components/NewTaskForm";
import { STACK_TASKS_TABLE_CELLS } from "./Table/Cells";
import { STACK_TASKS_SORT_CONFIG } from "./Table/constants";
import useStackTasksTableStore from "./Table/useTableStore";
import {
  STACK_TASKS_LIMIT,
  STACK_TASKS_LIMIT_STORAGE_KEY,
  STACK_TASKS_PAGINATION_OPTIONS,
} from "./constants";
import useStackTasksAnalytics from "./useStackTasksAnalytics";
import useStackTasks from "./useTasks";
import styles from "./styles.module.css";
import StackTasksEmpty from "./Empty";

const StackTasks = () => {
  const { viewer } = useTypedContext(AccountContext);
  const { stack: ctxStack } = useTypedContext(StackContext);
  const canCreateTaskFn = useCanCreateTask();

  const {
    tasks,
    error,
    isPageLoading,
    loadingContent,
    isEmpty,
    searchIsEmpty,
    onFetchItems,
    endCursor,
    startCursor,
    totalCount,
    paginationResetKey,
  } = useStackTasks(ctxStack.id);

  const trackAnalytics = useStackTasksAnalytics();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: onFetchItems,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: tasks.length,
    initialLimit: STACK_TASKS_LIMIT,
    storageKey: STACK_TASKS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const hasPagination = tasks.length > 0 || loadingContent;

  useTitle(`Tasks · ${ctxStack.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: ctxStack.name,
    },
  ]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  const stack = ctxStack;
  const canCreateTask = canCreateTaskFn(stack);

  return (
    <>
      <StackHeader />

      <PageInfoHeader title="Tasks">
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.STACK_TASKS}
          filtersOrderSettingsKey={STACK_TASKS_FILTER_TYPE}
          disabled={isEmpty}
        />
      </PageInfoHeader>

      <StackDetachedIntegrationCallout />
      {stack.lockedBy !== null && (
        <StackLockNotice lockedAt={stack.lockedAt} lockedBy={stack.lockedBy} viewer={viewer.id} />
      )}
      <PageWrapper margin="0">
        {(canCreateTask.result || canCreateTask.message) && (
          <NewTaskForm accessErrorMessage={canCreateTask.message} stackId={stack.id} redesigned />
        )}
        <TableSimple
          data={tasks}
          store={useStackTasksTableStore}
          ariaLabel="Stack tasks"
          cellPerColumn={STACK_TASKS_TABLE_CELLS}
          rowsPerPage={rowsPerPage}
          loading={loadingContent}
          wrapperClassName={styles.tableWrapper}
          filtersStorageKey={STACK_TASKS_FILTER_TYPE}
          sortConfig={STACK_TASKS_SORT_CONFIG}
          hasPagination={hasPagination}
          trackAnalytics={trackAnalytics}
          emptyState={
            <StackTasksEmpty hasNoSearchResult={searchIsEmpty} canWrite={stack.canWrite} />
          }
        />
        {hasPagination && (
          <TablePagination
            from={from}
            to={to}
            loading={loadingContent}
            goToNextPage={goToNextPage}
            setRowsPerPage={setRowsPerPage}
            goToPrevPage={goToPrevPage}
            goToFirstPage={goToFirstPage}
            goToLastPage={goToLastPage}
            rowsPerPage={rowsPerPage}
            itemsCount={totalCount}
            lastPage={lastPage}
            page={page}
            paginationOptions={STACK_TASKS_PAGINATION_OPTIONS}
          />
        )}
      </PageWrapper>
    </>
  );
};

export default StackTasks;
